import React, { useMemo, useState } from "react";
import { Navigation } from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Pencil, Trash2, SlidersHorizontal, Plus, X } from "lucide-react";
import {
  useParametersList,
  useCreateParameter,
  useUpdateParameter,
  useDeleteParameter,
} from "@/hooks/api/useParameters";
import type {
  ParameterItem,
  ParameterStatus,
} from "@/services/parameters.service";

const statusChip = (status: ParameterStatus) =>
  status === "active"
    ? "bg-[#135045] text-[#88C0B2]"
    : "bg-[#E8EFF0] text-[#3B494A]";

const pctFromDecimal = (decimalValue: string) => {
  const n = Number(decimalValue);
  if (!Number.isFinite(n)) return "0";
  return String(Math.round(n * 100 * 100) / 100); // keep 2 decimals max
};

const decimalFromPct = (pct: string) => {
  const n = Number(pct);
  if (!Number.isFinite(n)) return "0.00";
  return (Math.round((n / 100) * 10000) / 10000).toFixed(2); // 0.xx with 2 decimals
};

const formatCsatForDisplay = (value: string) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  const rounded = Math.round(n * 10) / 10;
  if (Math.abs(rounded - Math.round(rounded)) < 1e-9) return String(Math.round(rounded));
  return String(rounded);
};

const formatCsatForApi = (display: string) => {
  const n = Number(display);
  if (!Number.isFinite(n)) return "0";
  const rounded = Math.round(n * 10) / 10;
  // API example looks like "4" or "4.0". We'll keep minimal formatting.
  if (Math.abs(rounded - Math.round(rounded)) < 1e-9) return String(Math.round(rounded));
  return rounded.toFixed(1).replace(/\.0$/, "");
};

function getDisplayValue(name: string, apiValue: string) {
  if (name === "nota_de_volume" || name === "nota_de_custo") {
    return pctFromDecimal(apiValue);
  }
  if (name === "csat") return formatCsatForDisplay(apiValue);
  return apiValue;
}

function getApiValue(name: string, displayValue: string) {
  if (name === "nota_de_volume" || name === "nota_de_custo") {
    return decimalFromPct(displayValue);
  }
  if (name === "csat") return formatCsatForApi(displayValue);
  return displayValue;
}

export default function ParametersV2() {
  const { toast } = useToast();

  const [page, setPage] = useState(1);
  const [perPage] = useState(15);

  const { data, isLoading, isError, error, refetch } = useParametersList(
    { page, per_page: perPage },
    { enabled: true },
  );

  const parameters: ParameterItem[] = data?.data ?? [];
  const pagination = data?.pagination;

  const volumeParam = useMemo(
    () => parameters.find((p) => p.name === "nota_de_volume") ?? null,
    [parameters],
  );
  const costParam = useMemo(
    () => parameters.find((p) => p.name === "nota_de_custo") ?? null,
    [parameters],
  );

  const createMutation = useCreateParameter();
  const updateMutation = useUpdateParameter();
  const deleteMutation = useDeleteParameter();

  const [editOpen, setEditOpen] = useState(false);
  const [mode, setMode] = useState<"create" | "edit">("edit");

  const [formId, setFormId] = useState<number | null>(null);
  const [formName, setFormName] = useState("");
  const [formValue, setFormValue] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formStatus, setFormStatus] = useState<ParameterStatus>("active");
  const [saving, setSaving] = useState(false);

  const openEdit = (p: ParameterItem) => {
    setMode("edit");
    setFormId(p.id);
    setFormName(p.name);
    setFormValue(getDisplayValue(p.name, p.value));
    setFormDescription(p.description);
    setFormStatus(p.status);
    setEditOpen(true);
  };

  const openCreate = () => {
    setMode("create");
    setFormId(null);
    setFormName("");
    setFormValue("");
    setFormDescription("");
    setFormStatus("active");
    setEditOpen(true);
  };

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const requestDelete = (id: number) => {
    setDeleteId(id);
    setDeleteOpen(true);
  };

  const doDelete = async (id: number) => {
    try {
      await deleteMutation.mutateAsync(id);
      toast({ title: "Parâmetro deletado" });
      await refetch();
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ?? err?.message ?? "Erro ao deletar parâmetro.";
      toast({ title: "Erro", description: msg, variant: "destructive" });
    }
  };

  const handleSave = async () => {
    if (saving) return;
    if (!formName.trim()) {
      toast({ title: "Erro", description: "O campo `name` é obrigatório.", variant: "destructive" });
      return;
    }
    if (!formDescription.trim()) {
      toast({
        title: "Erro",
        description: "O campo `description` é obrigatório.",
        variant: "destructive",
      });
      return;
    }
    if (formValue.trim() === "") {
      toast({ title: "Erro", description: "O campo `value` é obrigatório.", variant: "destructive" });
      return;
    }

    const apiValue = getApiValue(formName, formValue);

    setSaving(true);
    try {
      // Regra do domínio: Volume + Custo = 100%
      if (mode === "edit" && formId != null) {
        if (formName === "nota_de_volume" && costParam) {
          const volumeDecimal = Number(apiValue);
          const costDecimal = (Math.round((1 - volumeDecimal) * 100) / 100).toFixed(2);

          await Promise.all([
            updateMutation.mutateAsync({
              id: formId,
              data: {
                name: formName,
                description: formDescription,
                status: formStatus,
                value: apiValue,
              },
            }),
            updateMutation.mutateAsync({
              id: costParam.id,
              data: {
                name: costParam.name,
                description: costParam.description,
                status: costParam.status,
                value: costDecimal,
              },
            }),
          ]);
        } else if (formName === "nota_de_custo" && volumeParam) {
          const costDecimal = Number(apiValue);
          const volumeDecimal = (Math.round((1 - costDecimal) * 100) / 100).toFixed(2);

          await Promise.all([
            updateMutation.mutateAsync({
              id: formId,
              data: {
                name: formName,
                description: formDescription,
                status: formStatus,
                value: apiValue,
              },
            }),
            updateMutation.mutateAsync({
              id: volumeParam.id,
              data: {
                name: volumeParam.name,
                description: volumeParam.description,
                status: volumeParam.status,
                value: volumeDecimal,
              },
            }),
          ]);
        } else {
          await updateMutation.mutateAsync({
            id: formId,
            data: {
              name: formName,
              description: formDescription,
              status: formStatus,
              value: apiValue,
            },
          });
        }
      } else {
        // create
        if (formName === "nota_de_volume" && costParam) {
          const volumeDecimal = Number(apiValue);
          const costDecimal = (Math.round((1 - volumeDecimal) * 100) / 100).toFixed(2);

          const created = await createMutation.mutateAsync({
            name: formName,
            value: apiValue,
            description: formDescription,
            status: formStatus,
          });

          // Atualiza o custo para manter 100% (se já existir)
          if (created?.id != null) {
            await updateMutation.mutateAsync({
              id: costParam.id,
              data: {
                name: costParam.name,
                description: costParam.description,
                status: costParam.status,
                value: costDecimal,
              },
            });
          }
        } else if (formName === "nota_de_custo" && volumeParam) {
          const costDecimal = Number(apiValue);
          const volumeDecimal = (Math.round((1 - costDecimal) * 100) / 100).toFixed(2);

          const created = await createMutation.mutateAsync({
            name: formName,
            value: apiValue,
            description: formDescription,
            status: formStatus,
          });

          if (created?.id != null) {
            await updateMutation.mutateAsync({
              id: volumeParam.id,
              data: {
                name: volumeParam.name,
                description: volumeParam.description,
                status: volumeParam.status,
                value: volumeDecimal,
              },
            });
          }
        } else {
          await createMutation.mutateAsync({
            name: formName,
            value: apiValue,
            description: formDescription,
            status: formStatus,
          });
        }
      }

      toast({ title: "Parâmetro salvo" });
      setEditOpen(false);
      await refetch();
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ?? err?.message ?? "Erro ao salvar parâmetro.";
      toast({ title: "Erro", description: msg, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const valueInput = () => {
    const isVolumeCost = formName === "nota_de_volume" || formName === "nota_de_custo";
    const isCsat = formName === "csat";
    if (isVolumeCost) {
      return (
        <Input
          type="number"
          inputMode="decimal"
          min={0}
          max={100}
          step={0.01}
          value={formValue}
          onChange={(e) => setFormValue(e.target.value)}
          className="h-12 rounded-xl border-[#BAC9CA] bg-[#F3FAFB]"
        />
      );
    }
    if (isCsat) {
      return (
        <Input
          type="number"
          inputMode="decimal"
          min={0}
          max={5}
          step={0.1}
          value={formValue}
          onChange={(e) => setFormValue(e.target.value)}
          className="h-12 rounded-xl border-[#BAC9CA] bg-[#F3FAFB]"
        />
      );
    }

    return (
      <Input
        type="text"
        value={formValue}
        onChange={(e) => setFormValue(e.target.value)}
        className="h-12 rounded-xl border-[#BAC9CA] bg-[#F3FAFB]"
      />
    );
  };

  const valueHint = () => {
    if (formName === "nota_de_volume") return "Informe em percentual (0 a 100). Ex.: 40.";
    if (formName === "nota_de_custo") return "Informe em percentual (0 a 100). Ex.: 60.";
    if (formName === "csat") return "Informe a nota de corte do CSAT (0 a 5).";
    return "Informe o valor exatamente como a API espera.";
  };

  return (
    <div className="flex min-h-screen bg-white">
      <Navigation />

      <main className="flex-1 flex flex-col">
        <div className="border-b border-[#BAC9CA] bg-white">
          <div className="flex items-center justify-between px-5 py-5">
            <div className="flex items-center gap-3">
              <SlidersHorizontal className="w-6 h-6 text-[#00646B]" />
              <h1 className="text-[28px] font-medium text-[#151C1D]">Parâmetros</h1>
            </div>

            <Button
              onClick={openCreate}
              className="h-12 px-4 rounded-xl bg-[#00646B] hover:bg-[#00646B]/90 text-white"
            >
              <Plus className="w-5 h-5 mr-2" />
              Adicionar
            </Button>
          </div>
        </div>

        <div className="flex-1 px-5 py-9">
          <div className="mb-4 text-sm text-[#3B494A]">
            Clique em <strong>Editar</strong> para abrir um formulário com todos os campos do parâmetro.
          </div>

          <div className="rounded-[28px] overflow-hidden border border-[#BAC9CA]">
            <div className="bg-[#EDF4F5] px-6 py-4 border-b border-[#BAC9CA]">
              <h2 className="text-lg font-medium text-[#151C1D]">Lista de Parâmetros</h2>
            </div>

            <div className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#EDF4F5] border-b border-[#BAC9CA] hover:bg-[#EDF4F5]">
                    <TableHead>Nome</TableHead>
                    <TableHead>Valor</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-28" />
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={5} className="h-32 text-center text-[#3B494A]">
                        Carregando...
                      </TableCell>
                    </TableRow>
                  ) : isError ? (
                    <TableRow>
                      <TableCell colSpan={5} className="h-32 text-center text-[#93000A]">
                        {error?.message ?? "Erro ao carregar parâmetros."}
                      </TableCell>
                    </TableRow>
                  ) : parameters.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="h-32 text-center text-[#3B494A]">
                        Nenhum parâmetro encontrado
                      </TableCell>
                    </TableRow>
                  ) : (
                    parameters.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell className="text-[#151C1D] font-medium">{p.name}</TableCell>
                        <TableCell className="text-[#3B494A]">
                          {getDisplayValue(p.name, p.value)}
                          {p.name === "nota_de_volume" || p.name === "nota_de_custo" ? "%" : ""}
                        </TableCell>
                        <TableCell className="text-[#3B494A]">{p.description}</TableCell>
                        <TableCell>
                          <Badge
                            className={`h-6 px-2 rounded ${statusChip(p.status)} text-xs font-semibold`}
                          >
                            {p.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            className="h-8 px-2 rounded hover:bg-[#0000000D]"
                            onClick={() => openEdit(p)}
                            disabled={saving}
                          >
                            <Pencil className="w-4 h-4 text-[#00646B]" />
                            <span className="ml-2 text-sm">Editar</span>
                          </Button>
                          <Button
                            variant="ghost"
                            className="h-8 px-2 rounded hover:bg-[#FFDAD6]"
                            onClick={() => requestDelete(p.id)}
                            disabled={saving || deleteMutation.isPending}
                          >
                            <Trash2 className="w-4 h-4 text-[#BA1A1A]" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {pagination && pagination.last_page > 1 && (
              <div className="flex items-center justify-end gap-2 px-4 py-3 bg-[#EDF4F5] border-t border-[#BAC9CA]">
                <Button
                  variant="ghost"
                  className="h-10 px-4"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  Anterior
                </Button>
                <span className="text-sm text-[#3B494A]">
                  Página {pagination.current_page} de {pagination.last_page}
                </span>
                <Button
                  variant="ghost"
                  className="h-10 px-4"
                  onClick={() => setPage((p) => Math.min(pagination.last_page, p + 1))}
                  disabled={page >= (pagination.last_page ?? page)}
                >
                  Próxima
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-[560px] p-0 overflow-hidden rounded-[28px] bg-white">
          <DialogHeader className="px-6 py-5 border-b border-[#BAC9CA] bg-[#EDF4F5]">
            <DialogTitle className="text-[22px] text-[#151C1D] font-medium">
              {mode === "create" ? "Adicionar parâmetro" : "Editar parâmetro"}
            </DialogTitle>
          </DialogHeader>

          <div className="px-6 py-5 space-y-4">
            <div className="space-y-2">
              <label className="text-xs font-medium text-[#3B494A]">Nome</label>
              <Input
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                className="h-12 rounded-xl border-[#BAC9CA] bg-[#F3FAFB]"
              />
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium text-[#3B494A]">Valor</label>
              {valueInput()}
              <div className="text-[12px] text-[#3B494A]">{valueHint()}</div>
              {(formName === "nota_de_volume" || formName === "nota_de_custo") && (
                <div className="text-[12px] text-[#93000A]">
                  Observação: Volume + Custo devem somar 100% (a tela mantém isso automaticamente).
                </div>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium text-[#3B494A]">Descrição</label>
              <Input
                type="text"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                className="h-12 rounded-xl border-[#BAC9CA] bg-[#F3FAFB]"
              />
            </div>

            <div className="space-y-2">
              <label className="text-xs font-medium text-[#3B494A]">Status</label>
              <Select value={formStatus} onValueChange={(v) => setFormStatus(v as ParameterStatus)}>
                <SelectTrigger className="h-12 rounded-xl border-[#BAC9CA] bg-[#F3FAFB]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">active</SelectItem>
                  <SelectItem value="inactive">inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1 h-12 rounded-xl border-[#BAC9CA] text-[#00646B]"
                onClick={() => setEditOpen(false)}
                disabled={saving}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                className="flex-1 h-12 rounded-xl bg-[#00646B] hover:bg-[#00646B]/90 text-white"
                disabled={saving || createMutation.isPending || updateMutation.isPending}
              >
                {saving ? "Salvando..." : "Salvar"}
              </Button>
            </div>

            <div className="flex items-center justify-end">
              {/* Mantém o X do UX sem depender do Dialog Close padrão */}
              <button
                onClick={() => setEditOpen(false)}
                className="text-[#3B494A] hover:opacity-80"
                aria-label="Fechar"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="max-w-[480px] rounded-[28px] p-0 overflow-hidden bg-white border-[#BAC9CA]">
          <AlertDialogHeader className="px-6 py-5 border-b border-[#BAC9CA] bg-[#EDF4F5]">
            <AlertDialogTitle className="text-[22px] text-[#151C1D] font-medium">
              Confirmar exclusão
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[#3B494A]">
              Tem certeza que deseja deletar este parâmetro?
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter className="px-6 py-5 gap-3">
            <AlertDialogCancel className="h-12 rounded-xl border-[#BAC9CA] text-[#00646B]">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteMutation.isPending || deleteId == null}
              className="h-12 rounded-xl bg-[#00646B] hover:bg-[#00646B]/90 text-white"
              onClick={() => {
                if (deleteId == null) return;
                void doDelete(deleteId).finally(() => setDeleteOpen(false));
              }}
            >
              Deletar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

